import { useNavigate } from "@tanstack/react-router";
import { useMemo } from "react";

import { useGetMe, useGetStore } from "@/api";
import { useInstagramConnection } from "@/hooks/instagram/useInstagramConnection";
import { resolveShopRoleKey } from "@/lib/settings/access";
import { useSessionStore } from "@/stores/session.store";

/** Last step after creating a shop: offer to connect its Instagram page. */
export function useInstagramOnboardingPage() {
  const navigate = useNavigate();
  const activeStoreId = useSessionStore((state) => state.activeStoreId);

  const { data: meData } = useGetMe();
  const me = meData?.status === 200 ? meData.data.data : undefined;
  const membership = me?.stores.find((store) => store.id === activeStoreId);
  const isOwner = useMemo(
    () => resolveShopRoleKey(membership?.role_name, me?.roles) === "owner",
    [membership?.role_name, me?.roles],
  );

  const { data: storeData } = useGetStore(activeStoreId ?? 0, {
    query: { enabled: activeStoreId !== null },
  });
  const storeName = storeData?.status === 200 ? storeData.data.data.name : "";

  const connection = useInstagramConnection(activeStoreId, activeStoreId !== null, isOwner);

  return {
    storeName,
    isConnected: connection.state === "connected",
    connection,
    onSkip: () => void navigate({ to: "/" }),
  };
}

export type InstagramOnboardingView = ReturnType<typeof useInstagramOnboardingPage>;
